import {
    Box,
    Button,
    HStack,
    Icon,
    Tag,
    TagCloseButton,
    TagLabel,
    Text,
    Textarea,
    useToast,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ResizeTextarea from 'react-textarea-autosize';
import { TbSend } from 'react-icons/tb';
import SessionContext from '../../contexts/SessionContext';
import { MentionableUser } from '../../hooks/useGetMentionableUsers';
import {
    useAddInvoiceNote,
    useGetInvoiceMentionableUsers,
    useGetInvoiceNotes,
} from '../../hooks/invoice/useInvoiceNotes';
import { positionLabel } from '../../utils/invitePositionOptions';
import { InvoiceDetailFoldableSection } from './InvoiceDetailFoldableSection';

const EMPTY_HINT = 'Mention a team member using the @ symbol.';

function renderNoteText(text: string, knownNames: string[]) {
    if (knownNames.length === 0) return text;
    const sortedNames = [...knownNames].sort((a, b) => b.length - a.length);
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;
    while (remaining.length > 0) {
        const atIndex = remaining.indexOf('@');
        if (atIndex === -1) {
            parts.push(remaining);
            break;
        }
        if (atIndex > 0) parts.push(remaining.slice(0, atIndex));
        const afterAt = remaining.slice(atIndex + 1);
        const matchedName = sortedNames.find(name => afterAt.startsWith(name));
        if (matchedName) {
            parts.push(
                <Text as="span" key={key++} color="#0071E3" fontWeight="semibold" fontSize="sm">
                    @{matchedName}
                </Text>,
            );
            remaining = remaining.slice(atIndex + 1 + matchedName.length);
        } else {
            parts.push('@');
            remaining = afterAt;
        }
    }
    return parts.length > 0 ? parts : text;
}

function formatCommentTime(dateString: string) {
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

export function InvoiceCommentsSection({ invoiceId }: { invoiceId: string }) {
    const sessionContext = useContext(SessionContext);
    const toast = useToast();
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const listEndRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(true);
    const [draft, setDraft] = useState('');
    const [showMentionMenu, setShowMentionMenu] = useState(false);
    const [mentionFilter, setMentionFilter] = useState('');
    const [mentionCursorPos, setMentionCursorPos] = useState(0);
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const [mentionedUsers, setMentionedUsers] = useState<MentionableUser[]>([]);

    const notesQuery = useGetInvoiceNotes(invoiceId);
    const { data: mentionableUsers } = useGetInvoiceMentionableUsers(invoiceId);
    const addNote = useAddInvoiceNote(invoiceId);
    const notes = notesQuery.data ?? [];

    const mentionNames = useMemo(() => {
        const names = (mentionableUsers ?? []).map(user => user.displayName);
        const self = `${sessionContext?.firstName ?? ''} ${sessionContext?.lastName ?? ''}`.trim();
        if (self && !names.includes(self)) names.push(self);
        return names;
    }, [mentionableUsers, sessionContext?.firstName, sessionContext?.lastName]);

    const filteredMentionUsers = useMemo(() => {
        const others = (mentionableUsers ?? []).filter(user => user._id !== sessionContext?.userId);
        if (!mentionFilter) return others;
        const lower = mentionFilter.toLowerCase();
        return others.filter(user => user.displayName.toLowerCase().includes(lower));
    }, [mentionableUsers, mentionFilter, sessionContext?.userId]);

    useEffect(() => {
        setSelectedMentionIndex(0);
    }, [mentionFilter]);

    useEffect(() => {
        listEndRef.current?.scrollIntoView({ block: 'nearest' });
    }, [notes.length]);

    const handleMentionSelect = useCallback((user: MentionableUser) => {
        const beforeCursor = draft.slice(0, mentionCursorPos);
        const atIndex = beforeCursor.lastIndexOf('@');
        if (atIndex === -1) return;
        const before = draft.slice(0, atIndex);
        const after = draft.slice(mentionCursorPos);
        setDraft(before + after);
        setMentionedUsers(prev => (prev.some(existing => existing._id === user._id) ? prev : [...prev, user]));
        setShowMentionMenu(false);
        setMentionFilter('');
        inputRef.current?.focus();
    }, [draft, mentionCursorPos]);

    const removeMention = useCallback((userId: string) => {
        setMentionedUsers(prev => prev.filter(user => user._id !== userId));
    }, []);

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const cursorPos = event.target.selectionStart || 0;
        setDraft(value);
        const beforeCursor = value.slice(0, cursorPos);
        const atMatch = beforeCursor.match(/@(\w*)$/);
        if (atMatch) {
            setShowMentionMenu(true);
            setMentionFilter(atMatch[1]);
            setMentionCursorPos(cursorPos);
        } else {
            setShowMentionMenu(false);
            setMentionFilter('');
        }
    };

    const hasComment = draft.trim().length > 0 || mentionedUsers.length > 0;
    const canSend = hasComment && !addNote.isPending;

    const handleSend = async () => {
        if (!canSend) return;
        const mentionPrefix = mentionedUsers.map(user => `@${user.displayName}`).join(' ');
        const trimmed = draft.trim();
        const note = mentionPrefix ? `${mentionPrefix}${trimmed ? ` ${trimmed}` : ''}` : trimmed;
        const explicitMentionIds = mentionedUsers.map(user => user._id);
        try {
            await addNote.mutateAsync({
                note,
                ...(explicitMentionIds.length > 0
                    ? { mentionedUserIds: explicitMentionIds, explicitMentionIds }
                    : {}),
            });
            setDraft('');
            setMentionedUsers([]);
            setShowMentionMenu(false);
        } catch {
            toast({
                title: 'Failed to send comment',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (showMentionMenu && filteredMentionUsers.length > 0) {
            if (event.key === 'Tab' || (event.key === 'Enter' && !event.metaKey && !event.ctrlKey)) {
                event.preventDefault();
                handleMentionSelect(filteredMentionUsers[selectedMentionIndex]);
                return;
            }
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setSelectedMentionIndex(prev => (prev < filteredMentionUsers.length - 1 ? prev + 1 : 0));
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setSelectedMentionIndex(prev => (prev > 0 ? prev - 1 : filteredMentionUsers.length - 1));
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                setShowMentionMenu(false);
                return;
            }
        }
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            void handleSend();
        }
    };

    return (
        <InvoiceDetailFoldableSection title="Comments" isOpen={isOpen} onToggle={setIsOpen} contentPadding={4}>
            <Box>
                {notesQuery.isLoading ? null : notesQuery.isError ? (
                    <Text fontSize="sm" color="red.500" mb={4}>
                        Couldn't load comments.
                    </Text>
                ) : notes.length === 0 ? (
                    <Text fontSize="sm" color="gray.900" mb={4}>
                        {EMPTY_HINT}
                    </Text>
                ) : (
                    <Box maxH="280px" overflowY="auto" mb={4}>
                        {notes.map((note, index) => (
                            <Box
                                key={note._id}
                                py={2}
                                borderTop={index === 0 ? undefined : '1px solid'}
                                borderColor="gray.100"
                            >
                                <HStack justify="space-between" align="baseline" spacing={3} mb={1}>
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                        {note.user}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500" flexShrink={0}>
                                        {formatCommentTime(note.timestamp)}
                                    </Text>
                                </HStack>
                                <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">
                                    {renderNoteText(note.note, mentionNames)}
                                </Text>
                            </Box>
                        ))}
                        <Box ref={listEndRef} />
                    </Box>
                )}

                <Box position="relative">
                    {showMentionMenu && filteredMentionUsers.length > 0 && (
                        <Box
                            position="absolute"
                            bottom="100%"
                            left={0}
                            right={0}
                            mb={1}
                            bg="white"
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="lg"
                            maxH="160px"
                            overflow="auto"
                            zIndex={10}
                        >
                            {filteredMentionUsers.map((user, index) => (
                                <Box
                                    key={user._id}
                                    px={3}
                                    py={2}
                                    cursor="pointer"
                                    bg={index === selectedMentionIndex ? 'gray.100' : undefined}
                                    _hover={{ bg: 'gray.100' }}
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        handleMentionSelect(user);
                                    }}
                                >
                                    <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                        {user.displayName}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                        {positionLabel(user.role, user.position)}
                                    </Text>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {mentionedUsers.length > 0 && (
                        <Wrap spacing={1} mb={2}>
                            {mentionedUsers.map(user => (
                                <WrapItem key={user._id}>
                                    <Tag size="sm" borderRadius="full" variant="solid" bg="#0071E3" color="white">
                                        <TagLabel fontSize="xs">{user.displayName}</TagLabel>
                                        <TagCloseButton onClick={() => removeMention(user._id)} />
                                    </Tag>
                                </WrapItem>
                            ))}
                        </Wrap>
                    )}

                    <HStack
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderRadius="lg"
                        px={3}
                        py={2}
                        spacing={2}
                        align="center"
                        _focusWithin={{ borderColor: 'gray.300' }}
                    >
                        <Box flex="1" minW={0}>
                            <Textarea
                                ref={inputRef}
                                as={ResizeTextarea}
                                value={draft}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                placeholder="Add a comment..."
                                resize="none"
                                minH="20px"
                                maxRows={6}
                                rows={1}
                                fontSize="sm"
                                lineHeight="20px"
                                border="none"
                                px="2px"
                                py="1px"
                                bg="transparent"
                                sx={{ '&::placeholder': { lineHeight: '20px' } }}
                                _hover={{ borderColor: 'transparent' }}
                                _focus={{ borderColor: 'transparent', boxShadow: 'none' }}
                                isDisabled={addNote.isPending}
                            />
                        </Box>
                        <Button
                            size="sm"
                            variant={hasComment ? 'solid' : 'outline'}
                            borderRadius="lg"
                            fontWeight="medium"
                            color={hasComment ? 'white' : 'gray.500'}
                            bg={hasComment ? '#0071E3' : undefined}
                            borderColor={hasComment ? '#0071E3' : 'gray.200'}
                            flexShrink={0}
                            leftIcon={<Icon as={TbSend as React.ElementType} boxSize={3.5} />}
                            _hover={hasComment ? { bg: '#005bb5' } : undefined}
                            _disabled={hasComment ? {
                                bg: '#0071E3',
                                color: 'white',
                                opacity: 0.7,
                                cursor: 'not-allowed',
                            } : undefined}
                            onClick={() => void handleSend()}
                            isLoading={addNote.isPending}
                            isDisabled={!canSend}
                        >
                            Send
                        </Button>
                    </HStack>
                </Box>
            </Box>
        </InvoiceDetailFoldableSection>
    );
}
